import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useAppDispatch } from '@/redux/hooks';
import { fetchFacetViewMore } from '@/redux/modules/search/search.actions';

type UseServerSideFacetExpansionParams = {
    facetId: string;
    size: number;
};

type UseServerSideFacetExpansionResult = {
    isLoading: boolean;
    onViewMoreClick: () => void;
};

/**
 * Gates the on-demand "View More" fetch for a server-paginated facet.
 *
 * Returns:
 *  - `onViewMoreClick` for `<ViewMore>`'s `onClick`. The first click
 *    after the URL last changed dispatches `fetchFacetViewMore` with
 *    the cap. Subsequent clicks (collapse / re-expand) are no-ops --
 *    the slice already holds the expanded options.
 *  - `isLoading` so the caller can render a spinner inside the
 *    expanded list while the server is fetching the larger bucket.
 *    Without this, the primitive's instant uncontrolled expansion only
 *    shows the same 6 default items until the response lands ~1-2s
 *    later, which reads as a broken expand.
 *
 * Visual expand/collapse state is owned by the Hammer UI `<ViewMore>`
 * primitive itself; this hook only tracks the fetch lifecycle.
 *
 * Known UX trade-off: clicking View More on facet A while AuctionHouse
 * typeahead is active does not preserve the typeahead. The dispatched
 * request only sends facetOverrides for facet A, so the response omits
 * `auctionHouseName`. Acceptable for v1.
 */
export const useServerSideFacetExpansion = ({
    facetId,
    size,
}: UseServerSideFacetExpansionParams): UseServerSideFacetExpansionResult => {
    const dispatch = useAppDispatch();
    const { search } = useLocation();
    const [hasFetched, setHasFetched] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        setHasFetched(false);
    }, [search]);

    const onViewMoreClick = useCallback(() => {
        if (hasFetched) {
            return;
        }
        setHasFetched(true);
        setIsLoading(true);
        // createAsyncThunk's dispatch returns a promise that resolves on
        // fulfilled or rejected -- either way we want the spinner gone.
        void dispatch(fetchFacetViewMore({ facetId, size })).finally(() => {
            setIsLoading(false);
        });
    }, [dispatch, facetId, hasFetched, size]);

    return { isLoading, onViewMoreClick };
};
